import fetchAPI, { IHttpError } from '../../../lib/utils/fetch-api'
import {
  setAppealId,
  setAppealsEnumsData,
  setAppealsEnumsFailed,
  setAppealsEnumsLoading,
  setAppealsFormData,
  setAppealsFormFailed,
  setAppealsFormLoading,
} from './actions'
import { handleStageConflictResponse } from '../../lib/stage-conflict'

type AppealsFormErrorPayload = { message: string; statusCode?: string | number }

function formatErrorPart(part: unknown): string {
  if (part == null) return ''
  if (typeof part === 'string') return part.trim()
  if (typeof part === 'number' || typeof part === 'boolean') return String(part)
  if (typeof part === 'object') {
    const obj = part as Record<string, unknown>
    if (typeof obj.detail === 'string' && obj.detail.trim()) return obj.detail.trim()
    if (typeof obj.message === 'string' && obj.message.trim()) return obj.message.trim()
    if (typeof obj.msg === 'string' && obj.msg.trim()) return obj.msg.trim()
    if (typeof obj.error === 'string' && obj.error.trim()) return obj.error.trim()
  }
  return ''
}

/** Нормализация errors/error из API и catch в безопасный payload для Modal/UI. */
export function normalizeAppealsFormError(
  error: unknown,
  statusCode?: string | number,
): AppealsFormErrorPayload {
  if (error == null || error === '') {
    return { message: 'Неизвестная ошибка', statusCode }
  }
  if (typeof error === 'string') {
    return { message: error.trim() || 'Неизвестная ошибка', statusCode }
  }
  if (Array.isArray(error)) {
    const joined = error.map(formatErrorPart).filter(Boolean).join(', ')
    // `[object Object]` от join сырых объектов — не годится
    if (joined && !joined.includes('[object Object]')) {
      return { message: joined, statusCode }
    }
    if (joined.includes('[object Object]')) {
      const fromObjects = error.map(formatErrorPart).filter(Boolean).join(', ')
      if (fromObjects) return { message: fromObjects, statusCode }
    }
    return { message: joined || 'Неизвестная ошибка', statusCode }
  }
  if (typeof error === 'object') {
    const obj = error as Record<string, unknown>
    const fromKnown =
      formatErrorPart(obj) ||
      (typeof obj.message === 'string' ? obj.message.trim() : '') ||
      (typeof obj.detail === 'string' ? obj.detail.trim() : '')
    if (fromKnown) {
      return {
        message: fromKnown,
        statusCode: statusCode ?? (obj.statusCode as string | number | undefined),
      }
    }
    // `{}` после JSON.stringify(Error) и прочие пустые объекты
    if (Object.keys(obj).length === 0) {
      return { message: 'Неизвестная ошибка', statusCode }
    }
    try {
      const raw = JSON.stringify(obj)
      if (raw && raw !== '{}') {
        return {
          message: raw,
          statusCode: statusCode ?? (obj.statusCode as string | number | undefined),
        }
      }
    } catch {
      /* ignore */
    }
    return {
      message: 'Неизвестная ошибка',
      statusCode: statusCode ?? (obj.statusCode as string | number | undefined),
    }
  }
  return { message: String(error), statusCode }
}

function failedFromResponse(response: Record<string, any>): AppealsFormErrorPayload {
  return normalizeAppealsFormError(response?.errors || response?.error || response?.error?.message)
}

function failedFromCatch(err: unknown): AppealsFormErrorPayload {
  const statusCode = (err as IHttpError)?.statusCode
  const message = (err as IHttpError)?.message
  if (message) {
    return normalizeAppealsFormError({ message, statusCode }, statusCode)
  }
  return normalizeAppealsFormError(err, statusCode || 'error')
}

export function getAppealsForm(id) {
  return async dispatch => {
    dispatch(setAppealsFormLoading())
    try {
      const response = (await fetchAPI(`/api/appeal/${id}`)) || []
      if (response['errors'] || response?.error) {
        return dispatch(setAppealsFormFailed(failedFromResponse(response)))
      }
      return dispatch(setAppealsFormData(response))
    } catch (err) {
      return dispatch(setAppealsFormFailed(failedFromCatch(err)))
    }
  }
}

export function nextStage(id, formData) {
  return async dispatch => {
    // dispatch(setAppealsFormLoading())
    try {
      const response = await fetchAPI(
        `/api/appeal/${id}`,
        {
          method: 'POST',
          body: JSON.stringify(formData),
        },
        true,
      )

      if (handleStageConflictResponse(response, () => dispatch(getAppealsForm(id)))) {
        return null
      }

      if (response?.errors || response?.error) {
        await dispatch(setAppealsFormFailed(failedFromResponse(response)))
        return null
      }
      await dispatch(setAppealsFormData(response))
      return response
    } catch (err) {
      await dispatch(setAppealsFormFailed(failedFromCatch(err)))
      return null
    }
  }
}

export function updateFormData(id, formData) {
  return async dispatch => {
    try {
      const response = await fetchAPI(`/api/appeal/${id}`, {
        method: 'POST',
        body: JSON.stringify(formData),
      })

      if (response['errors'] || response?.error) {
        await dispatch(setAppealsFormFailed(failedFromResponse(response)))
        return null
      }

      if ((!id || id === 'new') && response?.id) {
        await dispatch(setAppealId(response.id))
      }

      return response
    } catch (err) {
      await dispatch(setAppealsFormFailed(failedFromCatch(err)))
      return null
    }
  }
}

export function getAppealsEnums(id: string) {
  return async dispatch => {
    dispatch(setAppealsEnumsLoading())
    try {
      const response = await fetchAPI(`/api/appeal/${id}/enum`)
      if (response['errors'] || response?.error) {
        return dispatch(setAppealsEnumsFailed(failedFromResponse(response).message))
      }

      return dispatch(setAppealsEnumsData(response))
    } catch (err) {
      console.error(err)
      return dispatch(setAppealsEnumsFailed('При загрузки enum возникла ошибка'))
    }
  }
}
